import { srcSetFor, baseOf, SIZES } from "../lib/img";
import React, { useEffect, useState, useRef } from "react";
import { getFirestoreApi } from "../firebase";
import { cleanField, cleanEmail, cleanPhone, isHoneypotTripped } from "../lib/formSecurity";
import logo from "../assets/kidsology-logo.png";
import TripRegistrationModal from "../components/TripRegistrationModal";
import ScrollFilmBackdrop from "../components/ScrollFilmBackdrop";
import ScrollStory from "../components/ScrollStory";
import { PROGRAM_STEPS } from "../data/programs";
import SEO from "../components/SEO";
import LocaleLink from "../components/LocaleLink";
import SiteHeader from "../components/SiteHeader";
import { useLocale, useL } from "../i18n/LocaleContext";
import { track } from "../lib/analytics";
import { isDraftPath } from "../seo/site.mjs";
import { OFFER, HOME_OFFER, PROGRAMS, SECTION } from "../data/offer";
import { ARROW, CatalogCard, SectionHead } from "../components/catalog/CatalogParts";
import { DUBAI_WINTER_CAMP, DUBAI_WINTER_HERO } from "../data/trips";
import "./Home.css";

/* ─── SCROLL REVEAL HOOK ─────────────────────────────────────────── */
function useReveal(className = "reveal", threshold = 0.12, delay = 0) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.classList.add(className);
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => el.classList.add("visible"), delay);
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [className, threshold, delay]);
  return ref;
}

/* ─── DATA ───────────────────────────────────────────────────────── */
/* ── Scroll-film backdrop config ──────────────────────────────────
   One layer that runs as the fixed backdrop from page top through the
   end of the Programs section, then unmounts. Swapping films is a one
   line change: replace `src` and `poster`. `endSectionId` controls
   where the layer fades out.
   Owner is A/B testing the logo-assembly film first; the second
   candidate (child-journey film) can be dropped in by editing this
   constant only. */
const SCROLL_FILM = {
  /* Image-sequence on canvas (round 17). Pre-loaded JPGs, no decoder.
     Round 18 ends the assembly at Trips per owner spec: the logo
     locks together as the visitor finishes Trips so the final state
     is visible without having to scroll through Reviews / Contact.
     body.sfb-on stays on so the glass treatment continues across the
     whole site over the brand-gradient body. */
  poster:       "/videos/scroll-poster.jpg",
  /* Anchor for the assembly-finish position. Kept separate from the
     hash target `#trips` (which now lives on the Dubai card) so that
     the film still measures the whole Trips section's bottom edge. */
  endSectionId: "trips-end",
};

/* ─── SHARED COPY ─────────────────────────────────────────────────
   Triples used in more than one place on the page. Everything else
   lives inline next to its own markup (see I18N.md). `en` is the
   owner-approved English and must never be reworded. */
const C_NAV = {
  about:    { en: "About",    ar: "عن Kidsology", ru: "О нас" },
  programs: { en: "Programs", ar: "البرامج",      ru: "Программы" },
  trips:    { en: "Trips",    ar: "الرحلات",      ru: "Поездки" },
  reviews:  { en: "Reviews",  ar: "التقييمات",    ru: "Отзывы" },
  contact:  { en: "Contact",  ar: "تواصل معنا",   ru: "Контакты" },
};
const C_BOOK_CALL = { en: "Book a Discovery Call", ar: "احجز مكالمة تعريفية مجانية", ru: "Бесплатная консультация" };
const C_EXPLORE_PROGRAMS = { en: "Explore Programs", ar: "تعرّف على البرامج", ru: "Наши программы" };
const C_EXPLORE_TRIPS    = { en: "Explore Trips",    ar: "تعرّف على الرحلات", ru: "Наши поездки" };
const C_HERO_SECTIONS    = { en: "Our four sections", ar: "أقسامنا الأربعة", ru: "Четыре направления" }; /* aria only */
/* Hero programme line: the five programs (owner, 2026-10-06), names
   only, no links. */
const HERO_PROGRAMS = PROGRAMS.map((p) => p.name);
const C_BRAND_SUB = { en: "Trips, Camps & Programs", ar: "الرحلات والمخيمات والبرامج", ru: "Поездки, лагеря и программы" };
const C_EMAIL     = { en: "Email", ar: "البريد الإلكتروني", ru: "Email" };
const C_YOUR_NAME = { en: "Your name", ar: "اسمك", ru: "Ваше имя" };
const C_SENDING   = { en: "Sending…", ar: "جارٍ الإرسال…", ru: "Отправляем…" };
const C_RESPOND_24 = { en: " · We respond within 24 hours", ar: " · نرد خلال 24 ساعة", ru: " · Ответим в течение 24 часов" };

/* About — one paragraph that captures everything Kidsology does under
   one umbrella (owner spec 2026-06-10, replacing the earlier skills strip). */

/* Round 23: PROGRAMS data lifted to src/data/programs.js so the
   homepage grid and the /programs overview page render from the
   same source. Imported below as PROGRAMS_DATA. */

/* Kept as a documented fallback copy source for the copy team (the
   Featurable widget renders the live Google reviews instead), so
   these stay English only — nothing here reaches the page. */
const TESTIMONIALS = [
  { quote: "My son came home after the very first session and said Mum, I finally understand why I get nervous before matches. That one insight changed everything for him.", name: "Sarah A.", role: "Parent, Dubai",                    initial: "S", tag: "Mental Skills" },
  { quote: "We brought Kidsology into our academy and within three weeks we could see a measurable shift in how our players handled pressure and communicated with each other.", name: "Coach Ahmed", role: "Football Academy Director, Sharjah", initial: "A", tag: "Sports Psychology" },
  { quote: "My daughter was struggling with exam anxiety for two years. After six sessions she sat her mock exams with complete calm. The tools they teach are genuinely practical.", name: "Fatima H.", role: "Parent, Abu Dhabi",                initial: "F", tag: "1 on 1 Elite" },
  { quote: "The London trip was the best thing we ever signed our son up for. He came back a different person. More mature, more confident, and far more willing to take responsibility.", name: "Omar and Hind Y.", role: "Parents, Dubai",               initial: "O", tag: "Leadership Trip" },
  { quote: "What sets Kidsology apart is that they do not just talk at children. They engage them. Our students still reference the sessions months later.", name: "Ms. Rania K.", role: "Head of Year, Dubai International School", initial: "R", tag: "School Workshop" },
  { quote: "I was sceptical at first. Now I tell every parent I know. The change in my child's discipline and self-belief has been extraordinary.", name: "Mariam J.", role: "Parent, Dubai",                     initial: "M", tag: "Mental Skills" },
];

/* The single biggest pull-quote, rendered larger in the Reviews block. */
const FEATURED_REVIEW_INDEX = 3;  // the London trip / Omar & Hind quote

/* Trusted partners (owner clarification 2026-09-01): ELAC and Future
   Leaders Centre. Owner, 2026-10-09: add Poulton Consultancy (Cliff
   Poulton, Career Pathways) and Aivy (Coding with AI). Poulton's logo
   is their own file from their tiles; Aivy has no logo file, so its
   wordmark is cut from Aivy's own curriculum documents.
   `invert: true` means a light-on-dark logo that renders on a dark
   plate; `plate` sets that plate to the logo's own background colour
   so the logo has no visible box. Names are brands: same in all
   languages. */
const PARTNERS = [
  { name: "ELAC",                  logo: "/images/partners/elac.png",                  w: 300,  h: 181 },
  { name: "Future Leaders Centre", logo: "/images/partners/future-leaders-centre.png", w: 560,  h: 300 },
  { name: "Poulton Consultancy",   logo: "/images/partners/poulton-consultancy.png",   w: 480,  h: 81,  invert: true, plate: "#1A3A5C" },
  { name: "Aivy",                  logo: "/images/partners/aivy.png",                  w: 260,  h: 100 },
];

/* Communities and academies we have worked with — NOT partners.
   PSG Academy + Esperia Volleyball Academy = academies we worked with.
   Community Power = community we ran a free Mental Health Awareness Day
   class for. Shown as social proof, separately from the partners strip. */
const WORKED_WITH = [
  { name: "PSG Academy UAE",          logo: "/images/partners/psg-academy.jpg",        w: 1122, h: 1402 },
  { name: "Esperia Volleyball Academy", logo: "/images/partners/esperia-volleyball.jpg", w: 1586, h: 992 },
  { name: "Community Power",          logo: "/images/partners/community-power.png",    w: 469,  h: 161, invert: true },
];

/* ─── ICONS ───────────────────────────────────────────────────────── */
/* I_ARROW_RIGHT points along the reading direction, so it carries
   .i-arrow and index.css mirrors it under dir="rtl". The check, pin,
   quote and social marks must never mirror. */
const I_ARROW_RIGHT = <svg className="i-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>;
const I_CHECK = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
const I_PIN = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s7-7.58 7-13a7 7 0 1 0-14 0c0 5.42 7 13 7 13z"/><circle cx="12" cy="9" r="2.6"/></svg>;
const I_QUOTE = <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7.17 6C4.87 6 3 7.87 3 10.17v6.4h6.4v-6.4H6.4c0-1.53 1.24-2.77 2.77-2.77V6h-2zm10 0C14.87 6 13 7.87 13 10.17v6.4h6.4v-6.4h-3c0-1.53 1.24-2.77 2.77-2.77V6h-2.4z"/></svg>;
const I_WA = <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>;
const I_IG = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>;
const I_MAIL = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
const I_FB = <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.5-3.89 3.78-3.89 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 2.89h-2.33v6.99A10 10 0 0 0 22 12z"/></svg>;

/* ─── HOME ───────────────────────────────────────────────────────── */
export default function Home() {
  const { t } = useLocale();
  /* Display copy for the Dubai card. The RAW record still goes to
     TripRegistrationModal so the Firestore payload stays English. */
  const dubaiHero = useL(DUBAI_WINTER_HERO);
  const offer = useL(HOME_OFFER);
  const offerCards = useL(OFFER);
  const [stickyShow,   setStickyShow]   = useState(false);
  const [stickyDismiss,setStickyDismiss]= useState(false);
  const [dubaiOpen,    setDubaiOpen]    = useState(false);
  const [contactTab,   setContactTab]   = useState("parents");
  const actionVideoRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (!stickyDismiss) setStickyShow(y > window.innerHeight * 0.6);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [stickyDismiss]);

  /* Featurable Google Reviews widget.
     The Reviews section renders a container div with the
     data-featurable-async attribute. The Featurable bundle scans
     the DOM at load time for those containers and hydrates them
     with the live Google Reviews carousel. We inject the script
     AFTER React mounts (not from index.html) so the container
     exists when the script scans; otherwise the bundle aborts
     with "No Featurable containers found". */
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    /* The widget is 160 KB of third-party JavaScript. Load it only when
       the reviews section is about to scroll into view, so it never
       competes with the first paint (it was the heaviest script on
       the page in Lighthouse, 2026-10-08). */
    const load = () => {
      if (document.querySelector('script[data-featurable="1"]')) return;
      const s = document.createElement("script");
      s.src = "https://featurable.com/assets/bundle.js";
      s.defer = true;
      s.charset = "UTF-8";
      s.setAttribute("data-featurable", "1");
      document.body.appendChild(s);
    };
    const target = rReviews.current;
    if (!target || !("IntersectionObserver" in window)) {
      const id = window.setTimeout(load, 4000);
      return () => window.clearTimeout(id);
    }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) { load(); io.disconnect(); } },
      { rootMargin: "900px 0px" },
    );
    io.observe(target);
    return () => io.disconnect();
  }, []);

  /* Hash-nav resolver. Two responsibilities:
     1. #contact-partners flips the contact form to partnerships.
     2. Every hash target is re-aligned after mount so cross-route
        landings (e.g. `/#programs` from a program page) don't drift
        when the ScrollStory/hero/images fill in their reserved
        space. The browser's default hash jump fires before React
        mounts; we retry until the element exists, then rAF once so
        layout is committed, then compute the scroll target:
          - fits under the nav → center vertically in the space
            below the nav so the card sits in the middle of the page
          - taller than the available height → land the top of the
            target just under the nav with a 12px buffer
     */
  useEffect(() => {
    const alignToHash = () => {
      const raw = window.location.hash?.slice(1);
      if (!raw) return;
      if (raw === "contact-partners") setContactTab("partners");
      const targetId = raw === "contact-partners" ? "contact" : raw;

      let attempts = 0;
      const tryAlign = () => {
        const el = document.getElementById(targetId);
        if (!el) {
          if (attempts++ < 30) requestAnimationFrame(tryAlign);
          return;
        }
        requestAnimationFrame(() => {
          const navH = document.querySelector(".sh-bar")?.offsetHeight || 108;
          const rect = el.getBoundingClientRect();
          const elTopDoc = rect.top + window.scrollY;

          let scrollY;
          if (el.classList.contains("ss-pin")) {
            /* ScrollStory pinned outer container is N*100svh tall.
               Put the outer's top at y=0 so the sticky stage
               engages behind the fixed nav and fills the viewport. */
            scrollY = elTopDoc;
          } else {
            /* Everything else: land the target at the top of the
               scroll port, minus its own scroll-margin-top. Per-
               target vertical bias is expressed declaratively in
               CSS via scroll-margin-top so each landing can be
               tuned without JS. */
            const marginTop = parseFloat(
              getComputedStyle(el).scrollMarginTop || "0"
            ) || 0;
            scrollY = elTopDoc - marginTop;
          }
          window.scrollTo({ top: Math.max(0, scrollY), behavior: "auto" });
        });
      };
      tryAlign();
    };

    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    alignToHash();

    /* Re-align on any layout shift above the target. A
       ResizeObserver on <body> fires whenever an image decodes to
       its intrinsic size, a font swaps, or a reveal animation
       resolves — the exact events that leave the visitor 30–60px
       off after our first scroll. We also cover window.load and
       two safety timers. */
    const onLoad = () => alignToHash();
    if (document.readyState === "complete") onLoad();
    else window.addEventListener("load", onLoad, { once: true });

    const t1 = setTimeout(alignToHash, 500);
    const t2 = setTimeout(alignToHash, 1500);
    const t3 = setTimeout(alignToHash, 3000);

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => alignToHash());
      ro.observe(document.body);
      /* Stop the observer after 4s so it doesn't fire forever. */
      setTimeout(() => ro?.disconnect(), 4000);
    }

    window.addEventListener("hashchange", alignToHash);
    return () => {
      window.removeEventListener("hashchange", alignToHash);
      window.removeEventListener("load", onLoad);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      ro?.disconnect();
    };
  }, []);

  /* "Kidsology in Action" video (HLS adaptive stream, MP4 fallback).
     Nothing is requested on page load: the <video> has no source and
     preload="none", so only its poster shows.
       - within ~1200px of the viewport: the stream is attached and
         starts buffering (no playback yet);
       - at least a quarter on screen AND the visitor has paused
         scrolling for 150ms: it plays. Starting a video decoder
         mid-scroll caused a visible hitch, so playback never starts
         while the page is moving;
       - fully off screen: it pauses, so it never decodes in the
         background while the visitor scrolls the film.
     The autoplay attribute stays in the markup only as the hook for
     the reduced-motion rule in index.css, which hides the video for
     those visitors; they never load it at all. */
  useEffect(() => {
    const video = actionVideoRef.current;
    if (!video) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    video.autoplay = false;
    const hlsSrc = "/videos/hls/master.m3u8";
    let hls = null;
    let attached = false;
    let cancelled = false;
    let visible = false;
    let lastScroll = 0;
    let playTimer = 0;
    const schedulePlay = () => {
      clearTimeout(playTimer);
      playTimer = setTimeout(() => {
        if (cancelled || !visible) return;
        if (performance.now() - lastScroll < 150) { schedulePlay(); return; }
        if (video.paused) video.play().catch(() => {});
      }, 160);
    };
    const attach = () => {
      attached = true;
      video.preload = "auto";
      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        /* Safari plays HLS natively: no library needed at all. */
        video.src = hlsSrc;
        return;
      }
      /* Everyone else: hls.js, loaded on demand so the 540 KB library
         is not part of the main bundle every page has to download. */
      import("hls.js").then(({ default: Hls }) => {
        if (cancelled) return;
        if (Hls.isSupported()) {
          hls = new Hls({ capLevelToPlayerSize: true, startLevel: -1 });
          hls.loadSource(hlsSrc);
          hls.attachMedia(video);
        } else {
          video.src = "/videos/hero.mp4";
        }
        if (visible) schedulePlay();
      }).catch(() => { if (!cancelled) { video.src = "/videos/hero.mp4"; if (visible) schedulePlay(); } });
    };
    const onScroll = () => { lastScroll = performance.now(); };
    window.addEventListener("scroll", onScroll, { passive: true });
    if (typeof IntersectionObserver !== "function") {
      visible = true; attach(); schedulePlay();
      return () => { cancelled = true; clearTimeout(playTimer); window.removeEventListener("scroll", onScroll); if (hls) hls.destroy(); };
    }
    const near = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !attached) attach();
    }, { rootMargin: "1200px 0px" });
    const seen = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) {
        if (!attached) attach();
        schedulePlay();
      } else {
        clearTimeout(playTimer);
        if (!video.paused) video.pause();
      }
    }, { threshold: 0.25 });
    near.observe(video);
    seen.observe(video);
    return () => {
      cancelled = true;
      clearTimeout(playTimer);
      window.removeEventListener("scroll", onScroll);
      near.disconnect();
      seen.disconnect();
      if (hls) hls.destroy();
    };
  }, []);

  const rAbout    = useReveal("reveal", 0.10);
  const rTrips    = useReveal("reveal", 0.10);
  const rPrograms = useReveal("reveal", 0.10);
  const rReviews  = useReveal("reveal", 0.10);
  const rAction   = useReveal("reveal", 0.10);
  const rContact  = useReveal("reveal-scale", 0.18);

  return (
    <>
      <SEO
        title={t({
          en: "Trips, Programs & Workshops for Kids in Dubai | Kidsology",
          ar: "رحلات وبرامج ومسارات مهنية وورش عمل في دبي | Kidsology",
          ru: "Поездки, программы и воркшопы для детей в Дубае | Kidsology",
        })}
        description={t({
          en: "KHDA-approved in Dubai: international and domestic trips, five 8-session programs, career pathways and internships, and workshops with guest speakers.",
          ar: "معتمدة من KHDA في دبي: رحلات دولية ومحلية، وخمسة برامج من 8 جلسات، ومسارات مهنية وتدريب عملي، وورش عمل مع متحدثين ضيوف.",
          ru: "Одобрено KHDA, Дубай: международные и внутренние поездки, пять программ по 8 занятий, карьерные треки и стажировки, воркшопы с приглашёнными спикерами.",
        })}
        path="/"
        image="/images/team/rasmi-kids-1.jpg"
        preloadImages={[{ href: "/videos/frames/f-001.webp", as: "fetch", crossOrigin: "anonymous", type: "image/webp", media: "(min-width: 768px)" }]}
      />

      {/* ── Dubai registration modal (UK is a past trip now) ── */}
      <TripRegistrationModal
        open={dubaiOpen}
        onClose={() => setDubaiOpen(false)}
        trip={DUBAI_WINTER_CAMP}
        mode="earlyAccess"
      />

      {/* ── Floating contact: the WhatsApp bubble and the sticky bar,
          inside one landmark so assistive technology can reach them
          (axe "region", 2026-10-08). ── */}
      <aside className="home-float" aria-label={t({ en: "Quick contact", ar: "تواصل سريع", ru: "Быстрая связь" })}>
      <a
        className="wa-float"
        href="https://wa.me/971528360206"
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t({ en: "Chat on WhatsApp", ar: "تواصل معنا على واتساب", ru: "Написать в WhatsApp" })}
      >
        {I_WA}
      </a>

      {/* ── Sticky bar (appears after first scroll) ── */}
      {!stickyDismiss && (
        <div className={`sticky-bar${stickyShow ? " visible" : ""}`}>
          <p className="sticky-bar-text">
            <strong>{t({
              en: "Ready to invest in your child's mind?",
              ar: "هل أنت مستعد للاستثمار في عقل طفلك؟",
              ru: "Готовы вложиться в мышление вашего ребёнка?",
            })}</strong><span className="sticky-bar-text-sub">{t({
              en: " Book a free discovery call today.",
              ar: " احجز مكالمة تعريفية مجانية اليوم.",
              ru: " Запишитесь на бесплатную вводную консультацию.",
            })}</span>
          </p>
          <div className="sticky-bar-actions">
            <a href="#contact" className="btn btn-gold" style={{ padding: "11px 24px", fontSize: 14 }}>{t(C_BOOK_CALL)}</a>
            <button
              className="sticky-bar-dismiss"
              onClick={() => setStickyDismiss(true)}
              aria-label={t({ en: "Dismiss", ar: "إغلاق", ru: "Закрыть" })}
            >×</button>
          </div>
        </div>
      )}
      </aside>

      {/* Global header: src/components/SiteHeader.jsx (Edit 2). */}
      <SiteHeader tone="home" />

      {/* ══════════════════════════════════════════════════
          Scroll-film backdrop
          One fixed layer behind page content, visible from page top
          through the end of #programs, then unmounts. Falls back to
          no layer at all under reduced-motion / mobile / video-fail —
          in those cases the site renders exactly as it does today.
      ══════════════════════════════════════════════════ */}
      {/* enableMobile dropped 2026-06-16 (owner spec Option A):
          the scroll-film canvas no longer mounts under 768 px. On
          phones the body's navy → amber gradient is the only
          background — no figures, no letterbox, no seam. */}
      <ScrollFilmBackdrop
        poster={SCROLL_FILM.poster}
        endSectionId={SCROLL_FILM.endSectionId}
      />

      {/* ══════════════════════════════════════════════════
          1. HERO
          Content (badge, headline, paragraph, CTAs, trust line)
          renders directly over the scroll-film backdrop. Hero
          background is transparent so the film shows through.
          Soft navy gradient overlay only on the content column
          to keep the headline readable.
      ══════════════════════════════════════════════════ */}
      <main id="main">
      <section className="hero hero--sfb" id="hero">
        <div className="hero-content">
          {/* Edit 3 (2026-09-28): owner-approved hero copy in all three
              languages (I18N.md, "Hero"). One eyebrow, one two-part H1,
              one paragraph, two actions, one quiet programme line. No
              ages, no repeated credentials. */}
          <p className="hero-badge">
            <span className="hero-badge-dot" aria-hidden="true" />
            {t({
              en: "KHDA-Approved Youth Development in Dubai",
              ar: "برامج تنمية الأطفال واليافعين في دبي، معتمدة من KHDA",
              ru: "Программы развития детей и подростков в Дубае, одобренные KHDA",
            })}
          </p>

          <h1 className="hero-headline">
            <span className="hero-headline-line">
              {t({
                en: "We find what works best in the world.",
                ar: "نبحث عن أفضل الأساليب في العالم.",
                ru: "Мы находим лучшие методики в мире.",
              })}
            </span>{" "}
            <span className="hero-headline-line hero-headline-italic">
              {t({
                en: "Then we bring it to your child.",
                ar: "ثم نقدّمها لطفلك.",
                ru: "И адаптируем их для вашего ребёнка.",
              })}
            </span>
          </h1>

          <p className="hero-sub">
            {t({
              en: "Kidsology brings educational methods from around the world to the UAE, adapts them for children here, and personalizes each programme around how each child learns and develops.",
              ar: "تجلب Kidsology أساليب تعليمية من حول العالم إلى الإمارات، وتكيّفها مع الأطفال هنا، وتخصص كل برنامج بما يناسب طريقة تعلّم كل طفل ونموّه.",
              ru: "Kidsology привозит образовательные методики со всего мира в ОАЭ, адаптирует их для детей здесь и выстраивает каждую программу с учётом того, как учится и развивается каждый ребёнок.",
            })}
          </p>

          {/* The four main sections, the same as the menu (owner,
              2026-10-06: keep them in the main section as well). They
              stay visible on phones, where the menu folds away. */}
          <nav className="hero-sections" aria-label={t(C_HERO_SECTIONS)}>
            <ul>
              {offerCards.map((card) => (
                <li key={card.key}>
                  <LocaleLink
                    to={card.href}
                    className="hero-section-link"
                    onClick={() => track("hero_section_click", { section: card.key })}
                  >
                    <span>{card.title}</span>
                    {ARROW}
                  </LocaleLink>
                </li>
              ))}
            </ul>
          </nav>

          <div className="hero-actions">
            <a
              href="#contact"
              className="btn btn-gold-lg"
              onClick={() => track("hero_register_interest_click")}
            >
              {t({ en: "Register Interest", ar: "سجّل اهتمامك", ru: "Оставить заявку" })}
            </a>
          </div>

          {/* Quiet programme line: names only, no links, no ages. */}
          <p className="hero-programs">
            {HERO_PROGRAMS.map((name, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="hero-programs-sep" aria-hidden="true">·</span>}
                <span className="hero-programs-item">{t(name)}</span>
              </React.Fragment>
            ))}
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          1A. THE FOUR SECTIONS (owner, 2026-10-06)
          International & Domestic Trips, Programs, Career Pathways &
          Internships, Workshops & Guest Speakers: one card each, and
          pressing a card opens that section's page. Catalogue cards
          (src/components/catalog) on an opaque warm panel; `cat-scope`
          gives them the catalogue's photo rules outside a .cat-page.
      ══════════════════════════════════════════════════ */}
      <section className="home-offer sfb-panel cat-scope" id="offer" aria-labelledby="offer-title">
        <div className="cat-wrap">
          <SectionHead id="offer-title" eyebrow={offer.eyebrow} title={offer.title} lead={offer.lead} />
          <ul className="cat-grid cat-grid--four">
            {offerCards.map((card) => (
              <li key={card.key}>
                <CatalogCard href={card.href} photo={card.photo} eyebrow={card.eyebrow} title={card.title} text={card.text} more={offer.more} />
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          1B. RESEARCH STATS STRIP
          Real published studies that back why mental skills
          training matters. Sits between Hero and About. Same
          block + CSS that lived here at the round-1 baseline,
          restored 2026-06-11 per owner request.
          Stat figures stay Western digits in all three languages.
      ══════════════════════════════════════════════════ */}
      <div className="research-bar-wrap sfb-panel">
        <div className="research-bar-heading">
          {t({
            en: "Why mental skills matter",
            ar: "لماذا تهم المهارات الذهنية",
            ru: "Почему важны психологические навыки",
          })}
        </div>
        <div className="research-bar">
          {[
            {
              stat: "90%",
              label: {
                en: "of Olympic athletes train mentally to perform under pressure",
                ar: "من الرياضيين الأولمبيين يتدربون ذهنياً للأداء تحت الضغط",
                ru: "олимпийских спортсменов тренируют психику, чтобы выступать под давлением",
              },
              source: {
                en: "U.S. Olympic Committee Research",
                ar: "بحث اللجنة الأولمبية الأمريكية",
                ru: "Исследование Олимпийского комитета США",
              },
            },
            {
              stat: "270K+",
              label: {
                en: "students improved academically when taught mental skills",
                ar: "طالب تحسّن أداؤهم الدراسي عند تعليمهم المهارات الذهنية",
                ru: "учащихся улучшили академические результаты после обучения психологическим навыкам",
              },
              source: {
                en: "Durlak et al., Child Development Journal",
                ar: "Durlak وآخرون، مجلة Child Development",
                ru: "Durlak и др., журнал Child Development",
              },
            },
            {
              stat: "12,000",
              label: {
                en: "students showed a growth mindset lifts grades and cuts failure",
                ar: "طالب أظهروا أن عقلية النمو ترفع الدرجات وتقلّل الرسوب",
                ru: "учащихся показали, что установка на рост повышает оценки и снижает неуспеваемость",
              },
              source: {
                en: "Yeager & Dweck, Nature (2019)",
                ar: "Yeager وDweck، مجلة Nature (2019)",
                ru: "Yeager и Dweck, Nature (2019)",
              },
            },
            {
              stat: "50%",
              label: {
                en: "higher long-term success in children with focus and self-control",
                ar: "نجاح أعلى على المدى الطويل للأطفال الذين يتمتعون بالتركيز وضبط النفس",
                ru: "выше долгосрочная успешность у детей с концентрацией и самоконтролем",
              },
              source: {
                en: "McClelland et al., Early Childhood Research Quarterly",
                ar: "McClelland وآخرون، Early Childhood Research Quarterly",
                ru: "McClelland и др., Early Childhood Research Quarterly",
              },
            },
          ].map((r, i) => (
            <div className="research-bar-item" key={i}>
              <div className="research-bar-stat">{r.stat}</div>
              <div className="research-bar-label">{t(r.label)}</div>
              <div className="research-bar-source">{t(r.source)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════
          2. ABOUT
          Consolidated per owner spec (2026-06-10):
          skills strip → heading → founder bios → tagline → CTA.
          Copy preserved verbatim from previous .what + .who blocks.
      ══════════════════════════════════════════════════ */}
      <section className="about sfb-panel" ref={rAbout}>
        <div className="about-inner" id="about">
          {/* Round 24: eyebrow + heading span the full width above the
              2-col grid so the left text card and right founder card
              tops align pixel-for-pixel below. */}
          <div className="about-header">
            <span className="eyebrow">
              {t({ en: "About Kidsology", ar: "عن Kidsology", ru: "О Kidsology" })}
            </span>
            <h2 className="about-heading">
              {t({ en: "Built by Experts Who", ar: "بُني على يد خبراء", ru: "Создано специалистами," })}
              <br />
              <em>{t({ en: "Understand Children", ar: "يفهمون الأطفال", ru: "которые понимают детей" })}</em>
            </h2>
          </div>

          <div className="about-grid">
            <div className="about-grid-left">
              {/* The owner's verbatim positioning copy. Canonical
                  ar / ru from I18N.md — do not re-translate. */}
              <div className="about-lead">
                <p>
                  {t({
                    en: "At Kidsology, we study what the best-performing countries and institutions around the world do exceptionally well. We look at Japan for mental math and focus, leading business schools for entrepreneurship, and top sports systems for performance and discipline. We then turn these approaches into ECA programs designed to build the skills children need to become a stronger, more capable future generation.",
                    ar: "في Kidsology، ندرس ما تتفوق فيه أفضل الدول والمؤسسات في العالم. ننظر إلى اليابان في الحساب الذهني والتركيز، وإلى كبرى كليات إدارة الأعمال في ريادة الأعمال، وإلى أقوى الأنظمة الرياضية في الأداء والانضباط. ثم نحوّل هذه المناهج إلى برامج أنشطة لا صفية تبني المهارات التي يحتاجها الأطفال ليصبحوا جيلاً أقوى وأكثر قدرة.",
                    ru: "В Kidsology мы изучаем то, что лучшие страны и учреждения мира делают исключительно хорошо. Мы смотрим на Японию в устном счёте и концентрации, на ведущие бизнес-школы в предпринимательстве и на сильнейшие спортивные системы в результативности и дисциплине. Затем мы превращаем эти подходы в программы дополнительных занятий (ECA), которые развивают навыки, необходимые детям, чтобы стать более сильным и способным будущим поколением.",
                  })}
                </p>
                <p>
                  {t({
                    en: "Then we bring those proven approaches to the UAE and adapt them to the children here. Anna, our psychologist, and I combine this global research with our experience working with UAE schools and children to understand what they actually need.",
                    ar: "ثم ننقل هذه المناهج المجرّبة إلى الإمارات ونكيّفها مع الأطفال هنا. أنا وآنا، أخصائيتنا النفسية، نجمع بين هذه الأبحاث العالمية وخبرتنا في العمل مع مدارس الإمارات وأطفالها لنفهم ما يحتاجونه فعلاً.",
                    ru: "Затем мы переносим эти проверенные подходы в ОАЭ и адаптируем их под детей здесь. Я и Анна, наш психолог, соединяем это глобальное исследование с нашим опытом работы со школами и детьми в ОАЭ, чтобы понять, что им действительно нужно.",
                  })}
                </p>
                <p>
                  {t({
                    en: "And through our international camps, we take children directly to the places where those skills are being done best.",
                    ar: "ومن خلال مخيماتنا الدولية، نأخذ الأطفال مباشرةً إلى الأماكن التي تُتقن فيها هذه المهارات.",
                    ru: "А через наши международные лагеря мы отвозим детей прямо туда, где эти навыки развивают лучше всего.",
                  })}
                </p>
              </div>

              {/* Credibility pill row — unchanged. */}
              <div
                className="about-skills"
                role="list"
                aria-label={t({ en: "Kidsology credibility", ar: "مصداقية Kidsology", ru: "Достижения Kidsology" })}
              >
                {[
                  {
                    en: "Over a decade with children",
                    ar: "أكثر من عشر سنوات مع الأطفال",
                    ru: "Более десяти лет работы с детьми",
                  },
                  {
                    en: "Programs with PSG Academy Dubai",
                    ar: "برامج داخل أكاديمية PSG دبي",
                    ru: "Программы в академии PSG в Дубае",
                  },
                  {
                    en: "Programs with Esperia Volleyball Academy",
                    ar: "برامج داخل أكاديمية Esperia للكرة الطائرة",
                    ru: "Программы в волейбольной академии Esperia",
                  },
                  {
                    en: "Worldwide educational trips",
                    ar: "رحلات تعليمية حول العالم",
                    ru: "Образовательные поездки по всему миру",
                  },
                  {
                    en: "Methods designed by a psychologist",
                    ar: "أساليب من تصميم أخصائية نفسية",
                    ru: "Методики, разработанные психологом",
                  },
                  {
                    en: "Accredited programmes in Oxford",
                    ar: "برامج معتمدة في أوكسفورد",
                    ru: "Аккредитованные программы в Оксфорде",
                  },
                  {
                    en: "Programs across the UAE and Jordan",
                    ar: "برامج في الإمارات والأردن",
                    ru: "Программы в ОАЭ и Иордании",
                  },
                ].map((p, i) => (
                  <span className="about-skill" role="listitem" key={i}>{t(p)}</span>
                ))}
              </div>

              {/* Two CTAs — same component pair as the hero. */}
              <div className="about-cta-row">
                <a href="#programs" className="btn btn-gold-lg">{t(C_EXPLORE_PROGRAMS)}</a>
                <a href="#trips" className="btn btn-outline-navy">{t(C_EXPLORE_TRIPS)}</a>
              </div>
            </div>

            <div className="about-grid-right">
              <article className="about-person">
                <div className="about-person-photo">
                  <img
                    width="1066"
                    height="1600"
                    decoding="async"
                    loading="lazy"
                    srcSet={srcSetFor("/images/team/rasmi")}
                    sizes={SIZES.avatar}
                    src="/images/team/rasmi.jpg"
                    alt={t({
                      en: "Rasmi Bani Hani, Co-Founder of Kidsology",
                      ar: "رسمي بني هاني، الشريك المؤسس لـ Kidsology",
                      ru: "Расми Бани Хани, соучредитель Kidsology",
                    })}
                    loading="lazy"
                  />
                </div>
                <div className="about-person-body">
                  <h3 className="about-person-name">
                    {t({ en: "Rasmi Bani Hani", ar: "رسمي بني هاني", ru: "Расми Бани Хани" })}
                  </h3>
                  <p className="about-person-role">
                    {t({
                      en: "Youth Development Specialist · Co-Founder",
                      ar: "أخصائي تطوير الشباب · شريك مؤسس",
                      ru: "Специалист по развитию подростков · Соучредитель",
                    })}
                  </p>
                  <p>
                    {t({
                      en: "A former professional footballer who represented the Jordan National Team, Rasmi has spent over a decade developing young people. He turns elite performance habits into training any child can follow.",
                      ar: "لاعب كرة قدم محترف سابق مثّل المنتخب الوطني الأردني، أمضى رسمي أكثر من عشر سنوات في تطوير الشباب. يحوّل عادات الأداء النخبوي إلى تدريب يستطيع أي طفل اتباعه.",
                      ru: "Бывший профессиональный футболист, игравший за национальную сборную Иордании, Расми более десяти лет занимается развитием подростков. Он превращает привычки элитных спортсменов в тренировку, доступную любому ребёнку.",
                    })}
                  </p>
                </div>
              </article>

              <article className="about-person">
                <div className="about-person-photo">
                  <img
                    width="1066"
                    height="1600"
                    decoding="async"
                    loading="lazy"
                    srcSet={srcSetFor("/images/team/anna")}
                    sizes={SIZES.avatar}
                    src="/images/team/anna.jpg"
                    alt={t({
                      en: "Anna Podgornaya, Co-Founder of Kidsology",
                      ar: "آنا بودغورنايا، الشريكة المؤسسة لـ Kidsology",
                      ru: "Анна Подгорная, соучредитель Kidsology",
                    })}
                    loading="lazy"
                  />
                </div>
                <div className="about-person-body">
                  <h3 className="about-person-name">
                    {t({ en: "Anna Podgornaya", ar: "آنا بودغورنايا", ru: "Анна Подгорная" })}
                  </h3>
                  <p className="about-person-role">
                    {t({
                      en: "Psychologist & Co-Founder",
                      ar: "أخصائية نفسية وشريكة مؤسسة",
                      ru: "Психолог и соучредитель",
                    })}
                  </p>
                  <p>
                    {t({
                      en: "A qualified psychologist with a Bachelor's and Master's in Psychology, Anna designs the assessment and methods behind every Kidsology program. Real psychology, not guesswork.",
                      ar: "أخصائية نفسية مؤهلة تحمل بكالوريوس وماجستير في علم النفس، تصمم آنا التقييم والأساليب التي يقوم عليها كل برنامج في Kidsology. علم نفس حقيقي، لا تخمين.",
                      ru: "Квалифицированный психолог с бакалавриатом и магистратурой по психологии, Анна разрабатывает диагностику и методики каждой программы Kidsology. Настоящая психология, а не догадки.",
                    })}
                  </p>
                </div>
              </article>

              <p className="about-tagline">
                {t({
                  en: "Psychology. Performance. Real-world experience.",
                  ar: "علم نفس. أداء. خبرة عملية.",
                  ru: "Психология. Результативность. Практический опыт.",
                })}
              </p>
            </div>
          </div>

          <div className="about-close">
            <h3 className="about-close-title">
              {t({ en: "Invest in their mind.", ar: "استثمر في عقله.", ru: "Вложитесь в его мышление." })}
              <br />
              <em>{t({ en: "You shape their future.", ar: "أنت من يصنع مستقبله.", ru: "Вы формируете его будущее." })}</em>
            </h3>
            <p className="about-close-body">
              {t({
                en: "Book a free discovery call and see how Kidsology can help your child grow.",
                ar: "احجز مكالمة تعريفية مجانية واكتشف كيف يمكن لـ Kidsology أن يساعد طفلك على النمو.",
                ru: "Запишитесь на бесплатную вводную консультацию и узнайте, как Kidsology поможет вашему ребёнку расти.",
              })}
            </p>
            <div className="about-close-actions">
              <a href="#contact" className="btn btn-gold-lg">{t(C_BOOK_CALL)} {I_ARROW_RIGHT}</a>
              <a href="#programs" className="btn btn-outline-navy">{t(C_EXPLORE_PROGRAMS)}</a>
            </div>
            {/* The ↓ glyph points down the page, so it stays as-is in
                every language (never mirrored). */}
            <p className="about-close-pointer">
              {t({ en: "Meet our programs ↓", ar: "تعرّف على برامجنا ↓", ru: "Наши программы ↓" })}
            </p>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          3. TRIPS
          Single hero card for the Dubai Winter Development Camp
          (the trip that is now enrolling). Full lineup + past
          trips live at /trips.
      ══════════════════════════════════════════════════ */}
      <section className="home-trips sfb-panel" id="trips-end" ref={rTrips}>
        <div className="home-trips-inner">
          <div className="home-trips-header">
            <span className="eyebrow eyebrow--light">
              {t({ en: "Trips & Camps", ar: "الرحلات والمخيمات", ru: "Поездки и лагеря" })}
            </span>
            <h2 className="home-trips-title">
              {t({ en: "Development & Leadership", ar: "مخيمات ورحلات", ru: "Лагеря и поездки" })}
              <br />
              <em>{t({ en: "Camps and Trips.", ar: "للتطوير والقيادة.", ru: "для развития и лидерства." })}</em>
            </h2>
          </div>

          <article className="home-dubai-card" id="trips">
            <div className="home-dubai-card-media">
              <img
                srcSet={srcSetFor(baseOf(dubaiHero.photo))}
                sizes={SIZES.half}
                src={dubaiHero.photo}
                alt={dubaiHero.photoAlt}
                width="1600"
                height="1066"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="home-dubai-card-body">
              <span className="home-dubai-card-badge">{dubaiHero.badge}</span>
              <h3 className="home-dubai-card-title">{dubaiHero.title}</h3>
              <p className="home-dubai-card-lead">{dubaiHero.lead}</p>
              <p className="home-dubai-card-body-copy">{dubaiHero.body}</p>
              <div className="home-dubai-card-chips">
                {dubaiHero.factChips.map((chip, i) => (
                  <span key={i} className="home-dubai-card-chip">{chip}</span>
                ))}
              </div>
              <div className="home-dubai-card-actions">
                <button
                  type="button"
                  className="btn btn-gold-lg"
                  onClick={() => setDubaiOpen(true)}
                >
                  {t({ en: "Register Now", ar: "سجّل الآن", ru: "Зарегистрироваться" })} {I_ARROW_RIGHT}
                </button>
                <LocaleLink to={dubaiHero.detailHref} className="btn btn-outline-light">
                  {t({ en: "Learn more", ar: "اعرف المزيد", ru: "Подробнее" })}
                  <span className="visually-hidden">: {t({ en: "Dubai Winter Development Camp", ar: "مخيم دبي الشتوي للتطوير", ru: "Зимний лагерь развития в Дубае" })}</span>
                </LocaleLink>
              </div>
              <p className="home-dubai-card-reassurance">{dubaiHero.reassurance}</p>
            </div>
          </article>

          <div className="home-trips-foot">
            <LocaleLink to="/trips" className="btn btn-outline-light">
              {t({ en: "View all trips", ar: "كل الرحلات", ru: "Все поездки" })} {I_ARROW_RIGHT}
            </LocaleLink>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          4. PROGRAMS
          Round 49: the entire section IS the ScrollStory. Same
          PROGRAM_STEPS + light warm-panel variant as /programs, so
          the two placements are pixel-identical (spec R49-T1). The
          light variant sits opaquely over the scroll-film canvas —
          no need to fade the backdrop.
      ══════════════════════════════════════════════════ */}
      <section className="home-programs" ref={rPrograms}>
        <ScrollStory
          id="programs"
          kicker={t({ en: "Our Programs", ar: "برامجنا", ru: "Наши программы" })}
          title={t({
            en: "Everything a growing mind needs.",
            ar: "كل ما يحتاجه العقل النامي.",
            ru: "Всё, что нужно растущему мышлению.",
          })}
          steps={PROGRAM_STEPS}
          mediaSide="left"
          variant="light"
        />

        <div className="home-programs-foot">
          <p>
            {t({
              en: "Not sure which program is right? ",
              ar: "لست متأكداً من البرنامج المناسب؟ ",
              ru: "Не знаете, какая программа подойдёт? ",
            })}
            <a href="#contact">
              {t({
                en: "Book a free discovery call",
                ar: "احجز مكالمة تعريفية مجانية",
                ru: "Запишитесь на бесплатную вводную консультацию",
              })}
            </a>
            {t({
              en: " and we will guide you.",
              ar: " وسنرشدك.",
              ru: ", и мы поможем выбрать.",
            })}
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          5. REVIEWS
          Featurable Google Reviews widget (matches production).
          The Featurable script in public/index.html hydrates
          the [data-featurable-async] div with live Google
          Reviews. TESTIMONIALS + FEATURED_REVIEW_INDEX are kept
          in this file as a documented fallback source for the
          copy team; they no longer render. If Featurable ever
          fails, restore the pull-quote + grid render here.
          The widget's own chrome is third-party and stays in the
          language Google/Featurable serve it in.
      ══════════════════════════════════════════════════ */}
      <section className="reviews sfb-panel" id="reviews" ref={rReviews}>
        <div className="reviews-inner">
          <div className="reviews-header">
            <span className="eyebrow">{t(C_NAV.reviews)}</span>
            <h2 className="reviews-title">
              {t({
                en: "Parents who trusted Kidsology.",
                ar: "أهالٍ وثقوا بـ Kidsology.",
                ru: "Родители, которые доверились Kidsology.",
              })}
            </h2>
          </div>

          <div
            id="featurable-2f8f7919-0956-4ecf-94d0-d21a2aa53270"
            data-featurable-async
            className="reviews-google-widget"
          />

          <div className="reviews-mini-cta">
            <p>
              {t({
                en: "Hundreds of families already trust Kidsology.",
                ar: "مئات العائلات تثق بـ Kidsology بالفعل.",
                ru: "Сотни семей уже доверяют Kidsology.",
              })}
            </p>
            <a href="#contact" className="btn btn-gold">{t(C_BOOK_CALL)} {I_ARROW_RIGHT}</a>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          6. SEE KIDSOLOGY IN ACTION
          The previously-hero video, relocated.
          Original asset preserved at /videos/hero.mp4
          + HLS adaptive at /videos/hls/master.m3u8.
      ══════════════════════════════════════════════════ */}
      <section className="kid-action sfb-panel" ref={rAction}>
        <div className="kid-action-inner">
          <span className="eyebrow eyebrow--light">
            {t({
              en: "See Kidsology in action",
              ar: "شاهد Kidsology على أرض الواقع",
              ru: "Kidsology в действии",
            })}
          </span>
          <h2 className="kid-action-title">
            {t({
              en: "Sessions, workshops, trips.",
              ar: "جلسات وورش ورحلات.",
              ru: "Занятия, воркшопы, поездки.",
            })}
            <br />
            <em>{t({ en: "Children, building.", ar: "أطفال يبنون أنفسهم.", ru: "Дети, которые растут." })}</em>
          </h2>
          <p className="kid-action-sub">
            {t({
              en: "Real moments from our programs and trips. The work behind the words.",
              ar: "لحظات حقيقية من برامجنا ورحلاتنا. العمل الذي يقف خلف الكلام.",
              ru: "Реальные моменты из наших программ и поездок. Работа, которая стоит за словами.",
            })}
          </p>

          <div className="kid-action-stage">
            {/* Reduced-motion users get the poster as a still image
                (the autoplay video is hidden via index.css). */}
            <img
              className="kid-action-still"
              src="/videos/hero-poster.jpg"
              width="1920"
              height="1078"
              decoding="async"
              alt={t({
                en: "Kidsology session in action",
                ar: "جلسة Kidsology على أرض الواقع",
                ru: "Занятие Kidsology",
              })}
              loading="lazy"
            />
            <video
              ref={actionVideoRef}
              className="kid-action-video"
              autoPlay muted loop playsInline
              preload="none"
              poster="/videos/hero-poster.jpg"
            />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          7. CONTACT
          Reduced friction: Name / Phone or Email / I am a… /
          Message, large submit, clear success state, WA + IG.
      ══════════════════════════════════════════════════ */}
      <section className="contact sfb-panel" id="contact" ref={rContact}>
        <div className="contact-inner">
          <span className="eyebrow">
            {t({ en: "Start today", ar: "ابدأ اليوم", ru: "Начните сегодня" })}
          </span>
          <h2 className="contact-title">
            {t({ en: "Ready to invest in", ar: "هل أنت مستعد للاستثمار", ru: "Готовы вложиться" })}
            <br />
            <em>{t({ en: "your child's mind?", ar: "في عقل طفلك؟", ru: "в мышление вашего ребёнка?" })}</em>
          </h2>
          <p className="contact-body">
            {t({
              en: "One free conversation is all it takes. We will listen to your child's situation, answer your questions, and show you how Kidsology can help. No commitment. No pressure.",
              ar: "محادثة واحدة مجانية تكفي. سنستمع إلى حالة طفلك، ونجيب على أسئلتك، ونوضح لك كيف يمكن لـ Kidsology أن يساعد. بدون التزام. بدون ضغط.",
              ru: "Достаточно одного бесплатного разговора. Мы выслушаем ситуацию вашего ребёнка, ответим на ваши вопросы и покажем, чем может помочь Kidsology. Без обязательств. Без давления.",
            })}
          </p>

          <div
            className="contact-toggle"
            role="tablist"
            aria-label={t({ en: "Contact form type", ar: "نوع نموذج التواصل", ru: "Тип формы обращения" })}
          >
            <button
              type="button"
              role="tab"
              id="contact-tab-parents"
              aria-selected={contactTab === "parents"}
              aria-controls="contact-panel-parents"
              className={`contact-toggle-btn${contactTab === "parents" ? " is-active" : ""}`}
              onClick={() => setContactTab("parents")}
            >
              {t({ en: "For Parents", ar: "للأهالي", ru: "Для родителей" })}
            </button>
            <button
              type="button"
              role="tab"
              id="contact-tab-partners"
              aria-selected={contactTab === "partners"}
              aria-controls="contact-panel-partners"
              className={`contact-toggle-btn${contactTab === "partners" ? " is-active" : ""}`}
              onClick={() => setContactTab("partners")}
            >
              {t({ en: "For Partners", ar: "للشركاء", ru: "Для партнёров" })}
            </button>
          </div>

          {contactTab === "partners" && (
            <p className="contact-form-lead contact-form-lead--center">
              {t({
                en: "Partner with Kidsology. We have delivered programs with leading schools, academies, and cultural centres across the UAE and Jordan, and we build every partnership to make the school more valuable to its parents. Tell us what you have in mind.",
                ar: "كن شريكاً لـ Kidsology. قدّمنا برامج مع مدارس وأكاديميات ومراكز ثقافية رائدة في الإمارات والأردن، ونبني كل شراكة لتجعل المدرسة أكثر قيمة لأهالي طلابها. أخبرنا بما يدور في ذهنك.",
                ru: "Станьте партнёром Kidsology. Мы проводили программы с ведущими школами, академиями и культурными центрами в ОАЭ и Иордании и строим каждое партнёрство так, чтобы школа стала ценнее для родителей её учеников. Расскажите, что у вас на уме.",
              })}
            </p>
          )}

          <div
            id="contact-panel-parents"
            role="tabpanel"
            aria-labelledby="contact-tab-parents"
            hidden={contactTab !== "parents"}
            className="contact-form-stage"
          >
            <ContactForm />
          </div>
          <div
            id="contact-panel-partners"
            role="tabpanel"
            aria-labelledby="contact-tab-partners"
            hidden={contactTab !== "partners"}
            className="contact-form-stage"
          >
            <PartnershipForm />
          </div>

          {/* Phone numbers, emails, handles and the Latin parts of the
              address are wrapped in <bdi> so they do not reorder in
              the right-to-left layout. */}
          <div className="contact-channels">
            <a href="https://wa.me/971528360206" target="_blank" rel="noopener noreferrer" className="contact-channel contact-channel--wa">
              <span className="contact-channel-ico" aria-hidden="true">{I_WA}</span>
              <div>
                <strong>WhatsApp</strong>
                <span><bdi>+971 52 836 0206</bdi></span>
              </div>
            </a>
            <a href="https://www.instagram.com/kidsology.me" target="_blank" rel="noopener noreferrer" className="contact-channel contact-channel--ig">
              <span className="contact-channel-ico" aria-hidden="true">{I_IG}</span>
              <div>
                <strong>Instagram</strong>
                <span><bdi>@kidsology.me</bdi></span>
              </div>
            </a>
            <a href="https://www.facebook.com/profile.php?id=61569261805218" target="_blank" rel="noopener noreferrer" className="contact-channel contact-channel--fb">
              <span className="contact-channel-ico" aria-hidden="true">{I_FB}</span>
              <div>
                <strong>Facebook</strong>
                <span><bdi>Kidsology Learning Centre</bdi></span>
              </div>
            </a>
            <a href="mailto:info@kidsology.ae" className="contact-channel contact-channel--mail">
              <span className="contact-channel-ico" aria-hidden="true">{I_MAIL}</span>
              <div>
                <strong>{t(C_EMAIL)}</strong>
                <span><bdi>info@kidsology.ae</bdi></span>
              </div>
            </a>
            <a
              href="https://www.google.com/maps/search/?api=1&query=I+Rise+Tower+Office+19C06+Dubai+United+Arab+Emirates"
              target="_blank"
              rel="noopener noreferrer"
              className="contact-channel contact-channel--addr"
            >
              <span className="contact-channel-ico" aria-hidden="true">{I_PIN}</span>
              <div>
                <strong>{t({ en: "Visit us", ar: "زُرنا", ru: "Приезжайте к нам" })}</strong>
                <span>
                  <bdi>I Rise Tower</bdi>
                  {t({ en: ", Office ", ar: "، مكتب ", ru: ", офис " })}
                  <bdi>19C06</bdi>
                  {t({
                    en: ", Dubai, United Arab Emirates",
                    ar: "، دبي، الإمارات العربية المتحدة",
                    ru: ", Дубай, Объединённые Арабские Эмираты",
                  })}
                </span>
              </div>
            </a>
          </div>

          <div className="contact-reassure">
            {[
              { en: "Free", ar: "مجاناً", ru: "Бесплатно" },
              { en: "No obligation", ar: "بدون التزام", ru: "Без обязательств" },
              { en: "We respond within 24 hours", ar: "نرد خلال 24 ساعة", ru: "Ответим в течение 24 часов" },
              { en: "Dubai and UAE", ar: "دبي والإمارات", ru: "Дубай и ОАЭ" },
              { en: "Online sessions available", ar: "جلسات عبر الإنترنت متاحة", ru: "Доступны онлайн-занятия" },
              { en: "Ages 5 to 18", ar: "من 5 إلى 18 عاماً", ru: "от 5 до 18 лет" },
            ].map((c, i) => (
              <span className="contact-reassure-item" key={i}>
                <span className="contact-reassure-tick" aria-hidden="true">{I_CHECK}</span>{t(c)}
              </span>
            ))}
          </div>
        </div>
      </section>
      </main>

      {/* ── FOOTER ── */}
      <footer className="footer">
        {/* Partner + community/academy social-proof strips.
            Two rows, same visual pattern, different labels — so the
            partner vs academy distinction stays clear. */}
        <div
          className="footer-trust"
          aria-label={t({
            en: "Trusted partners and academies",
            ar: "الشركاء والأكاديميات",
            ru: "Партнёры и академии",
          })}
        >
          <PartnerStrip
            label={t({ en: "Trusted partners", ar: "شركاء موثوقون", ru: "Надёжные партнёры" })}
            items={PARTNERS}
            speed={40}
          />
          <PartnerStrip
            label={t({
              en: "Communities & academies we work with",
              ar: "المجتمعات والأكاديميات التي نعمل معها",
              ru: "Сообщества и академии, с которыми мы работаем",
            })}
            items={WORKED_WITH}
            reverse
            speed={32}
          />
        </div>

        <div className="footer-top">
          <div className="footer-col footer-brand-col">
            <div className="footer-brand-row">
              <img src={logo} alt="Kidsology" className="footer-logo" width="192" height="192" loading="lazy" />
              <span className="footer-brand-name">Kidsology <span>{t(C_BRAND_SUB)}</span></span>
            </div>
            <p className="footer-tagline">
              {t({
                en: "Training Minds. Shaping Futures. Dubai's youth development lab for children aged 5 to 18.",
                ar: "نُدرّب العقول. نصنع المستقبل. مختبر تطوير الشباب في دبي للأطفال من 5 إلى 18 عاماً.",
                ru: "Тренируем мышление. Формируем будущее. Лаборатория развития детей и подростков в Дубае, от 5 до 18 лет.",
              })}
            </p>
            <p className="footer-partner-cta">
              {t({
                en: "Are you a school, academy, or brand? ",
                ar: "هل أنت مدرسة أو أكاديمية أو علامة تجارية؟ ",
                ru: "Вы школа, академия или бренд? ",
              })}
              <a href="#contact-partners">
                {t({ en: "Partner with us", ar: "كن شريكاً لنا", ru: "Станьте нашим партнёром" })}
              </a>.
            </p>
            <div className="footer-social">
              <a href="https://www.instagram.com/kidsology.me" target="_blank" rel="noopener noreferrer" className="footer-social-link" aria-label={t({ en: "Instagram", ar: "إنستغرام", ru: "Instagram" })}>{I_IG}</a>
              <a href="https://wa.me/971528360206" target="_blank" rel="noopener noreferrer" className="footer-social-link" aria-label={t({ en: "WhatsApp", ar: "واتساب", ru: "WhatsApp" })}>{I_WA}</a>
              <a href="mailto:info@kidsology.ae" className="footer-social-link" aria-label={t(C_EMAIL)}>{I_MAIL}</a>
            </div>
          </div>

          <div className="footer-col">
            <p className="footer-col-title">{t({ en: "Site", ar: "الموقع", ru: "Сайт" })}</p>
            <ul className="footer-links">
              <li><a href="#about">{t(C_NAV.about)}</a></li>
              <li><LocaleLink to="/trips">{t(SECTION.trips)}</LocaleLink></li>
              <li><LocaleLink to="/programs">{t(SECTION.programs)}</LocaleLink></li>
              <li><LocaleLink to="/career-pathways">{t(SECTION.pathways)}</LocaleLink></li>
              <li><LocaleLink to="/workshops">{t(SECTION.workshops)}</LocaleLink></li>
              <li><LocaleLink to="/schools">{t(SECTION.schools)}</LocaleLink></li>
              <li><a href="#contact">{t(C_NAV.contact)}</a></li>
            </ul>
          </div>

          <div className="footer-col">
            <p className="footer-col-title">{t(C_NAV.programs)}</p>
            <ul className="footer-links">
              {PROGRAMS.map((p) => (
                <li key={p.id}><LocaleLink to={p.page || `/programs#${p.id}`}>{t(p.name)}</LocaleLink></li>
              ))}
            </ul>
          </div>

          <div className="footer-col">
            <p className="footer-col-title">{t(C_NAV.contact)}</p>
            <ul className="footer-links">
              <li><a href="https://wa.me/971528360206" target="_blank" rel="noopener noreferrer">WhatsApp</a></li>
              <li><a href="mailto:info@kidsology.ae"><bdi>info@kidsology.ae</bdi></a></li>
              <li><a href="https://www.instagram.com/kidsology.me" target="_blank" rel="noopener noreferrer"><bdi>@kidsology.me</bdi></a></li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <p className="footer-copy">
            {t({
              en: "© 2026 Kidsology Trips, Camps & Programs · All Rights Reserved · Dubai, UAE",
              ar: "© 2026 Kidsology للرحلات والمخيمات والبرامج · جميع الحقوق محفوظة · دبي، الإمارات",
              ru: "© 2026 Kidsology Поездки, лагеря и программы · Все права защищены · Дубай, ОАЭ",
            })}
          </p>
          <div className="footer-legal">
            {/* Each policy appears once it is published (DRAFT_ROUTES in
                src/seo/site.mjs). The old "#privacy" and "#terms" links
                pointed at nothing (audit, 2026-10-08). */}
            {!isDraftPath("/privacy-policy") && (
              <LocaleLink to="/privacy-policy">
                {t({ en: "Privacy Policy", ar: "سياسة الخصوصية", ru: "Политика конфиденциальности" })}
              </LocaleLink>
            )}
            {!isDraftPath("/refund-policy") && (
              <LocaleLink to="/refund-policy">
                {t({ en: "Refund and Cancellation Policy", ar: "سياسة الاسترداد والإلغاء", ru: "Политика возврата и отмены" })}
              </LocaleLink>
            )}
          </div>
        </div>
      </footer>
    </>
  );
}

/* ─── PARTNER STRIP ──────────────────────────────────────────────── */
function PartnerStrip({ label, items, reverse = false, speed = 40 }) {
  /* Bulletproof infinite marquee. The track is two identical copies
     of `items` so something is always entering the right edge as
     something else leaves the left edge. Position is updated by
     requestAnimationFrame with an explicit modulo wrap — no CSS
     animation iteration boundary means no "restart" instant. Hover
     pauses; reduced-motion centers the row statically.
     The track keeps direction: ltr under RTL (see Home.css) because
     the transform maths below is written for a left-to-right track
     and the content is logos only. */
  const trackRef = useRef(null);
  const halfRef  = useRef(0);
  const pausedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const track = trackRef.current;
    if (!track) return;

    const measure = () => {
      /* Half = total width / 2 because the track is exactly two copies. */
      halfRef.current = track.scrollWidth / 2;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(track);

    let raf;
    let pos = reverse ? -halfRef.current : 0;
    let lastT = performance.now();
    const dir = reverse ? 1 : -1;

    const tick = (t) => {
      const dt = Math.min(0.05, (t - lastT) / 1000);
      lastT = t;
      if (!pausedRef.current && halfRef.current > 0) {
        pos += dir * speed * dt;
        /* Modulo wrap keeps pos within (-half, 0]. The end of copy A
           and the start of copy B are byte-identical, so the wrap is
           visually undetectable. */
        if (pos <= -halfRef.current) pos += halfRef.current;
        if (pos >= 0)                pos -= halfRef.current;
        track.style.transform = `translate3d(${pos}px, 0, 0)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [reverse, speed]);

  const renderItem = (p, idx, copy) => (
    <li
      className={`footer-partner${p.invert ? " footer-partner--invert" : ""}`}
      key={`${copy}-${p.name}-${idx}`}
      aria-hidden={copy === "b" ? "true" : undefined}
      style={p.plate ? { background: p.plate } : undefined}
    >
      <img
        className="footer-partner-logo"
        src={p.logo}
        alt={p.name}
        width={p.w}
        height={p.h}
        loading="lazy"
        decoding="async"
        onError={(e) => {
          e.currentTarget.style.display = "none";
          const sib = e.currentTarget.nextElementSibling;
          if (sib) sib.style.display = "inline-block";
        }}
      />
      <span className="footer-partner-text" style={{ display: "none" }}>
        {p.name}
      </span>
    </li>
  );

  return (
    <div className="footer-partners" aria-label={label}>
      <span className="footer-partners-label">{label}</span>
      <div
        className="footer-partners-marquee"
        onMouseEnter={() => { pausedRef.current = true; }}
        onMouseLeave={() => { pausedRef.current = false; }}
      >
        <ul ref={trackRef} className="footer-partners-list footer-partners-track">
          {items.map((p, i) => renderItem(p, i, "a"))}
          {items.map((p, i) => renderItem(p, i, "b"))}
        </ul>
      </div>
    </div>
  );
}

/* ─── DISCOVERY CALL FORM (parents) ──────────────────────────────────
   Submits to homeContactLeads. The `recipient` field is the email the
   submission should be forwarded to once the owner wires up a Firebase
   Trigger Email extension (or equivalent) on this collection. Captured
   at write time so the routing intent is durable.
   The payload is identical in every language: the Firestore rules pin
   these exact fields and values, so only the visible labels change. */
function ContactForm() {
  const { t } = useLocale();
  const [form, setForm] = useState({ name: "", contact: "", childAge: "", message: "", website: "" });
  const [status, setStatus] = useState("idle");
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    /* Honeypot tripped -> pretend success without writing. Bots think
       they succeeded and don't retry. */
    if (isHoneypotTripped(form.website)) {
      setStatus("done");
      return;
    }
    const name    = cleanField(form.name, 80);
    const contact = cleanField(form.contact, 120);
    if (!name || !contact) return;
    setStatus("sending");
    try {
      const { db, collection, addDoc, serverTimestamp } = await getFirestoreApi();
      await addDoc(collection(db, "homeContactLeads"), {
        kind:      "discovery_call",
        recipient: "info@kidsology.ae",
        name,
        contact,
        childAge:  cleanField(form.childAge, 40),
        message:   cleanField(form.message, 2000),
        createdAt: serverTimestamp(),
      });
      track("lead_submit", { form: "home_contact" });
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div className="contact-form-done" role="status" aria-live="polite">
        <div className="contact-form-done-tick">{I_CHECK}</div>
        <h3>
          {t({
            en: "Thank you. We have your message.",
            ar: "شكراً لك. وصلتنا رسالتك.",
            ru: "Спасибо. Мы получили ваше сообщение.",
          })}
        </h3>
        <p>
          {t({
            en: "Our team will be in touch within 24 hours.",
            ar: "سيتواصل فريقنا معك خلال 24 ساعة.",
            ru: "Наша команда свяжется с вами в течение 24 часов.",
          })}
        </p>
      </div>
    );
  }

  return (
    <form className="contact-form" onSubmit={submit} noValidate>
      {/* Honeypot. Hidden from real users via .hp-field. Bots scrape
          all form fields and fill them, so we drop the submission
          when this comes back non-empty. Never shown to a human, so
          it stays English. */}
      <div className="hp-field" aria-hidden="true">
        <label htmlFor="cf-website">Website</label>
        <input id="cf-website" type="text" name="website" tabIndex={-1} autoComplete="off"
          value={form.website} onChange={set("website")} />
      </div>
      <div className="contact-field">
        <label htmlFor="cf-name">{t(C_YOUR_NAME)}</label>
        <input id="cf-name" type="text" autoComplete="name"
          placeholder={t({ en: "e.g. Sarah Ahmed", ar: "مثال: سارة أحمد", ru: "например: Сара Ахмед" })}
          maxLength={80}
          value={form.name} onChange={set("name")} required />
      </div>
      <div className="contact-field">
        <label htmlFor="cf-contact">
          {t({ en: "Phone or email", ar: "الهاتف أو البريد الإلكتروني", ru: "Телефон или email" })}
        </label>
        {/* dir="ltr" in every language: a phone number or email must
            not reorder while it is being typed. */}
        <input id="cf-contact" type="text" autoComplete="tel" dir="ltr"
          placeholder={t({
            en: "+971 5X XXX XXXX or your@email.com",
            ar: "+971 5X XXX XXXX أو your@email.com",
            ru: "+971 5X XXX XXXX или your@email.com",
          })}
          maxLength={120}
          value={form.contact} onChange={set("contact")} required />
      </div>
      <div className="contact-field">
        <label htmlFor="cf-age">
          {t({ en: "Child's age (optional)", ar: "عمر الطفل (اختياري)", ru: "Возраст ребёнка (необязательно)" })}
        </label>
        <input id="cf-age" type="text"
          placeholder={t({ en: "e.g. 9, or 7 and 11", ar: "مثال: 9، أو 7 و11", ru: "например: 9 или 7 и 11" })}
          maxLength={40}
          value={form.childAge} onChange={set("childAge")} />
      </div>
      <div className="contact-field contact-field--full">
        <label htmlFor="cf-message">
          {t({ en: "Anything else? (optional)", ar: "أي تفاصيل أخرى؟ (اختياري)", ru: "Что-то ещё? (необязательно)" })}
        </label>
        <textarea id="cf-message" rows={3}
          placeholder={t({
            en: "Tell us about your child or what you are hoping to achieve",
            ar: "أخبرنا عن طفلك أو ما تتمنى تحقيقه",
            ru: "Расскажите о ребёнке или о том, чего хотите достичь",
          })}
          maxLength={2000}
          value={form.message} onChange={set("message")} />
      </div>

      {status === "error" && (
        <p className="contact-form-error" role="alert">
          {t({
            en: "Something went wrong sending your message. Please try WhatsApp instead.",
            ar: "حدث خطأ أثناء إرسال رسالتك. جرّب واتساب من فضلك.",
            ru: "Не удалось отправить сообщение. Напишите нам в WhatsApp.",
          })}
        </p>
      )}

      <button type="submit" className="btn btn-gold-lg contact-form-submit" disabled={status === "sending"}>
        {status === "sending"
          ? t(C_SENDING)
          : t({
              en: "Book My Free Discovery Call",
              ar: "احجز مكالمتي التعريفية المجانية",
              ru: "Записаться на консультацию",
            })} {status !== "sending" && I_ARROW_RIGHT}
      </button>
      <p className="contact-form-note">
        {t({
          en: "Free · No obligation · We respond within 24 hours",
          ar: "مجاناً · بدون التزام · نرد خلال 24 ساعة",
          ru: "Бесплатно · Без обязательств · Ответим в течение 24 часов",
        })}
      </p>
    </form>
  );
}

/* ─── PARTNERSHIP FORM (schools / academies / brands / education
   partners). Submits to partnershipLeads with recipient
   partnership@kidsology.ae (singular, per owner spec 2026-06-13).
   Same rule as the discovery form: the payload, including the select
   `value`s, stays English in every language. */
function PartnershipForm() {
  const { t } = useLocale();
  const [form, setForm] = useState({
    name: "", org: "", role: "", email: "", phone: "", kind: "", message: "", website: "",
  });
  const [status, setStatus] = useState("idle");
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    if (isHoneypotTripped(form.website)) {
      setStatus("done");
      return;
    }
    const name  = cleanField(form.name, 80);
    const org   = cleanField(form.org, 120);
    const email = cleanEmail(form.email);
    const kind  = cleanField(form.kind, 30);
    const msg   = cleanField(form.message, 2000);
    if (!name || !org || !email.valid || !kind || !msg) {
      setStatus("error");
      return;
    }
    setStatus("sending");
    try {
      const { db, collection, addDoc, serverTimestamp } = await getFirestoreApi();
      await addDoc(collection(db, "partnershipLeads"), {
        kind:         "partnership",
        recipient:    "partnership@kidsology.ae",
        contactName:  name,
        organisation: org,
        role:         cleanField(form.role, 80),
        email:        email.value,
        phone:        cleanField(form.phone, 40),
        partnerType:  kind,
        message:      msg,
        createdAt:    serverTimestamp(),
      });
      track("lead_submit", { form: "home_partnership" });
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div className="contact-form-done" role="status" aria-live="polite">
        <div className="contact-form-done-tick">{I_CHECK}</div>
        <h3>
          {t({
            en: "Thank you. We have your enquiry.",
            ar: "شكراً لك. وصلنا طلبك.",
            ru: "Спасибо. Мы получили ваш запрос.",
          })}
        </h3>
        <p>
          {t({
            en: "Our partnerships team will be in touch within 24 hours.",
            ar: "سيتواصل فريق الشراكات معك خلال 24 ساعة.",
            ru: "Наша команда по партнёрствам свяжется с вами в течение 24 часов.",
          })}
        </p>
      </div>
    );
  }

  return (
    <form className="contact-form contact-form--partner" onSubmit={submit} noValidate>
      <div className="hp-field" aria-hidden="true">
        <label htmlFor="pf-website">Website</label>
        <input id="pf-website" type="text" name="website" tabIndex={-1} autoComplete="off"
          value={form.website} onChange={set("website")} />
      </div>
      <div className="contact-field">
        <label htmlFor="pf-name">{t(C_YOUR_NAME)}</label>
        <input id="pf-name" type="text" autoComplete="name"
          placeholder={t({ en: "e.g. Coach Rasmi", ar: "مثال: الكابتن رسمي", ru: "например: тренер Расми" })}
          maxLength={80}
          value={form.name} onChange={set("name")} required />
      </div>
      <div className="contact-field">
        <label htmlFor="pf-org">
          {t({ en: "Company or organisation", ar: "الشركة أو المؤسسة", ru: "Компания или организация" })}
        </label>
        <input id="pf-org" type="text" autoComplete="organization"
          placeholder={t({ en: "e.g. PSG Academy Dubai", ar: "مثال: أكاديمية PSG دبي", ru: "например: академия PSG в Дубае" })}
          maxLength={120}
          value={form.org} onChange={set("org")} required />
      </div>
      <div className="contact-field">
        <label htmlFor="pf-role">
          {t({ en: "Role (optional)", ar: "الدور (اختياري)", ru: "Должность (необязательно)" })}
        </label>
        <input id="pf-role" type="text" autoComplete="organization-title"
          placeholder={t({ en: "e.g. Head of Academy", ar: "مثال: مدير الأكاديمية", ru: "например: руководитель академии" })}
          maxLength={80}
          value={form.role} onChange={set("role")} />
      </div>
      <div className="contact-field">
        <label htmlFor="pf-email">{t(C_EMAIL)}</label>
        <input id="pf-email" type="email" autoComplete="email" dir="ltr" placeholder="you@org.com"
          maxLength={200}
          value={form.email} onChange={set("email")} required />
      </div>
      <div className="contact-field">
        <label htmlFor="pf-phone">
          {t({ en: "WhatsApp or phone (optional)", ar: "واتساب أو الهاتف (اختياري)", ru: "WhatsApp или телефон (необязательно)" })}
        </label>
        <input id="pf-phone" type="tel" autoComplete="tel" dir="ltr" placeholder="+971 5X XXX XXXX"
          maxLength={40}
          value={form.phone} onChange={set("phone")} />
      </div>
      <div className="contact-field">
        <label htmlFor="pf-kind">
          {t({ en: "Type of partnership", ar: "نوع الشراكة", ru: "Тип партнёрства" })}
        </label>
        {/* The option `value`s are part of the Firestore payload and
            stay English; only the labels are translated. */}
        <select id="pf-kind" value={form.kind} onChange={set("kind")} required>
          <option value="">{t({ en: "Choose one", ar: "اختر واحداً", ru: "Выберите" })}</option>
          <option value="school">{t({ en: "School", ar: "مدرسة", ru: "Школа" })}</option>
          <option value="academy">{t({ en: "Sports academy", ar: "أكاديمية رياضية", ru: "Спортивная академия" })}</option>
          <option value="brand">{t({ en: "Brand or sponsor", ar: "علامة تجارية أو راعٍ", ru: "Бренд или спонсор" })}</option>
          <option value="education">{t({ en: "Education partner", ar: "شريك تعليمي", ru: "Образовательный партнёр" })}</option>
          <option value="other">{t({ en: "Other", ar: "أخرى", ru: "Другое" })}</option>
        </select>
      </div>
      <div className="contact-field contact-field--full">
        <label htmlFor="pf-message">{t({ en: "Message", ar: "الرسالة", ru: "Сообщение" })}</label>
        <textarea id="pf-message" rows={4}
          placeholder={t({
            en: "Tell us what you have in mind",
            ar: "أخبرنا بما يدور في ذهنك",
            ru: "Расскажите, что у вас на уме",
          })}
          maxLength={2000}
          value={form.message} onChange={set("message")} required />
      </div>

      {status === "error" && (
        <p className="contact-form-error" role="alert">
          {t({
            en: "Something went wrong sending your message. Please try WhatsApp or email instead.",
            ar: "حدث خطأ أثناء إرسال رسالتك. جرّب واتساب أو البريد الإلكتروني من فضلك.",
            ru: "Не удалось отправить сообщение. Напишите нам в WhatsApp или на email.",
          })}
        </p>
      )}

      <button type="submit" className="btn btn-gold-lg contact-form-submit" disabled={status === "sending"}>
        {status === "sending"
          ? t(C_SENDING)
          : t({
              en: "Send partnership enquiry",
              ar: "أرسل طلب الشراكة",
              ru: "Отправить запрос",
            })} {status !== "sending" && I_ARROW_RIGHT}
      </button>
      <p className="contact-form-note">
        {t({ en: "Goes to ", ar: "يُرسل إلى ", ru: "Отправляется на " })}
        <bdi>partnership@kidsology.ae</bdi>
        {t(C_RESPOND_24)}
      </p>
    </form>
  );
}
